import { ApiResponse } from "../types";
import { Constants } from "../utils/constants";

export interface BuildingAddress {
  id?: number;
  buildingName: string;
  city: string;
  country: string;
  digitalAddress?: string;
  images: string[];
  latitude: number;
  longitude: number;
  postalCode: string;
  region: string;
  streetAddress: string;
  timezone: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateBuildingAddressPayload {
  buildingName: string;
  city: string;
  country: string;
  digitalAddress?: string;
  images: string[];
  latitude: number;
  longitude: number;
  postalCode: string;
  region: string;
  streetAddress: string;
  timezone: string;
  floors?: Array<{
    id?: number;
    floorNumber: number;
    label?: string;
    remove?: boolean;
    floorPlan?: { layout: any[] };
  }>;
}

class BuildingAddressService {
  private baseURL: string;

  constructor() {
    this.baseURL = Constants.SPACE_MANAGEMENT_API_BASE_URL;
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<ApiResponse> {
    const url = `${this.baseURL}${endpoint}`;

    // Get auth token with fallback like apiV2Service
    let token = localStorage.getItem("authToken");
    if (!token) {
      token = localStorage.getItem("access_token");
    }

    const defaultOptions: RequestInit = {
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    };

    const response = await fetch(url, { ...defaultOptions, ...options });

    // tolerate 204/empty responses
    let data: any = null;
    const contentType = response.headers.get("Content-Type") || "";
    if (contentType.includes("application/json")) {
      try {
        data = await response.json();
      } catch (_) {
        data = null;
      }
    }

    return {
      status: response.status,
      data,
      message: data?.message || "",
    };
  }

  // Get all building addresses
  async getAllBuildingAddresses(): Promise<ApiResponse> {
    return this.makeRequest("/api/v2/building-addresses");
  }

  // Get building addresses with pagination
  async getBuildingAddressesWithPagination(page = 0, size = 10, sort?: string): Promise<ApiResponse> {
    let endpoint = `/api/v2/building-addresses?page=${page}&size=${size}`;
    if (sort) {
      endpoint += `&sort=${sort}`;
    }
    return this.makeRequest(endpoint);
  }

  // Create a new building address
  async createBuildingAddress(buildingAddress: CreateBuildingAddressPayload): Promise<ApiResponse> {
    return this.makeRequest("/api/v2/building-addresses", {
      method: "POST",
      body: JSON.stringify(buildingAddress),
    });
  }

  // Update an existing building address
  async updateBuildingAddress(
    id: number,
    buildingAddress: Partial<CreateBuildingAddressPayload>
  ): Promise<ApiResponse> {
    return this.makeRequest(`/api/v2/building-addresses/${id}`, {
      method: "PATCH",
      body: JSON.stringify(buildingAddress),
    });
  }

  // Edit a building address (e.g., floors / floor plans)
  async patchBuildingAddress(id: number, requestBody: unknown): Promise<ApiResponse> {
    return this.makeRequest(`/api/v2/building-addresses/${id}`, {
      method: "PATCH",
      body: JSON.stringify(requestBody),
    });
  }

  // Delete a building address
  async deleteBuildingAddress(id: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v2/building-addresses/${id}`, {
      method: "DELETE",
    });
  }

  // Delete a floor from a building
  async deleteFloor(buildingId: number, floorId: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v2/building-addresses/${buildingId}/floors/${floorId}`, {
      method: "DELETE",
    });
  }

  // Get building address by ID
  async getBuildingAddressById(id: number): Promise<ApiResponse> {
    return this.makeRequest(`/api/v2/building-addresses/${id}`);
  }

  // Search building addresses
  async searchBuildingAddresses(params?: {
    buildingName?: string;
    city?: string;
    country?: string;
    digitalAddress?: string;
    region?: string;
    streetAddressContains?: string;
    zoneId?: number;
    pageNumber?: number;
    pageSize?: number;
    paged?: boolean;
    offset?: number;
    sort?: {
      sorted?: boolean;
      unsorted?: boolean;
    };
  }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();

    if (params?.buildingName) queryParams.append("buildingName", params.buildingName);
    if (params?.city) queryParams.append("city", params.city);
    if (params?.country) queryParams.append("country", params.country);
    if (params?.digitalAddress) queryParams.append("digitalAddress", params.digitalAddress);
    if (params?.region) queryParams.append("region", params.region);
    if (params?.streetAddressContains) queryParams.append("streetAddressContains", params.streetAddressContains);
    if (params?.zoneId !== undefined) queryParams.append("zoneId", params.zoneId.toString());
    if (params?.pageNumber !== undefined) queryParams.append("page", params.pageNumber.toString());
    if (params?.pageSize !== undefined) queryParams.append("size", params.pageSize.toString());
    if (params?.paged !== undefined) queryParams.append("paged", params.paged.toString());
    if (params?.offset !== undefined) queryParams.append("offset", params.offset.toString());
    if (params?.sort?.sorted !== undefined) queryParams.append("sort.sorted", params.sort.sorted.toString());
    if (params?.sort?.unsorted !== undefined) queryParams.append("sort.unsorted", params.sort.unsorted.toString());

    const queryString = queryParams.toString();
    const endpoint = `/api/v2/building-addresses/search${queryString ? `?${queryString}` : ""}`;

    return this.makeRequest(endpoint);
  }

  // Get accessible building addresses
  async getAccessibleBuildingAddresses(params?: {
    pageNumber?: number;
    pageSize?: number;
    sort?: {
      sorted?: boolean;
      unsorted?: boolean;
    };
    hasSpaces?: boolean;
  }): Promise<ApiResponse> {
    const queryParams = new URLSearchParams();

    if (params?.pageNumber !== undefined) queryParams.append("page", params.pageNumber.toString());
    if (params?.pageSize !== undefined) queryParams.append("size", params.pageSize.toString());
    if (params?.sort?.sorted !== undefined) queryParams.append("sort.sorted", params.sort.sorted.toString());
    if (params?.sort?.unsorted !== undefined) queryParams.append("sort.unsorted", params.sort.unsorted.toString());
    if (params?.hasSpaces !== undefined) queryParams.append("hasSpaces", params.hasSpaces.toString());

    const queryString = queryParams.toString();
    const endpoint = `/api/v2/building-addresses/accessible${queryString ? `?${queryString}` : ""}`;

    return this.makeRequest(endpoint);
  }
}

export const buildingAddressService = new BuildingAddressService();
export default buildingAddressService;
